'use client';

import PostMessage from '@components/PostMessage';
import Typography from '@ui/Typography';
import { isEmpty } from 'lodash-es';
import React, { useState } from 'react';
import { Rating } from '@mui/material';
import CommunityReviewsDialog from '@/components/next-page-components/CommunityReviewsDialog/CommunityReviewsDialog';
import { useGetCommunityRating } from '@/actions/nonprofit.actions';
import Chip from '@ui/Chip';
import ClickableTooltipWrapper from '@/components/Styled/ClickableTooltipWrapper';
import Image from 'next/image';
import { formatNumberForLocale } from '@/utils/stringHelpers';
import { MdInfo } from 'react-icons/md';
import useInfoModalStore from '@/components/store/infoModalSlice';

function ProfileTitle(props) {
  const { nonProfit, slug, addReview } = props;

  const setShowInfoModal = useInfoModalStore((state) => state.setOpen);

  const {
    data: nonProfitRating,
    isLoading: isnonProfitRatingLoading,
    refetch: refetchNonProfitRating,
  } = useGetCommunityRating(nonProfit.id);

  const { totalRating = 0, reviewCount = 0 } =
    nonProfitRating || nonProfit.rating || {};

  const [showReviews, setShowReviews] = useState(!!addReview);

  const averageRating =
    reviewCount > 0 ? Math.round((totalRating / reviewCount) * 10) / 10 : 0;
  const npoLocation = [nonProfit.city, nonProfit.state, nonProfit.country]
    .filter((i) => !!i)
    .join(', ');

  const handleReviewClick = () => {
    setShowReviews(true);
  };

  const handleShowInfo = () => {
    setShowInfoModal(true, {
      logoUrl: '/assets/images/neki-heart.png',
      title: 'Neki Score',
      name: 'Neki Score',
      description:
        'Your Neki Score reflects the total points your community members earn by taking meaningful action—giving, volunteering, engaging and celebrating goodness.',
    });
  };

  const renderReview = () => {
    return (
      <Typography
        variant="subtitle1"
        color="primary"
        className="cursor-pointer hover:underline"
        onClick={handleReviewClick}>
        {reviewCount
          ? `${reviewCount} Neki review${reviewCount !== 1 ? 's' : ''}`
          : ''}
      </Typography>
    );
  };

  return (
    <div>
      <div className="flex w-full flex-nowrap items-center justify-between gap-3 lg:justify-start lg:pr-2">
        <Typography variant="h2" component="h2">
          {nonProfit.name}
        </Typography>
      </div>
      <div className="my-1 flex flex-row gap-2">
        {averageRating > 0 && (
          <Typography variant="subtitle1" color="textSecondary">
            {averageRating.toFixed(1)}
          </Typography>
        )}

        <div
          onClick={handleReviewClick}
          className="hover:cursor-pointer"
          title="Write a review">
          <Rating
            name="read-only"
            value={averageRating}
            precision={0.1}
            readOnly
            className="text-primary"
          />
        </div>

        {renderReview()}
        {showReviews && (
          <CommunityReviewsDialog
            slug={slug}
            nonProfit={nonProfit}
            onClose={() => setShowReviews(false)}
            refetchNonProfitRating={refetchNonProfitRating}
            communityRating={nonProfitRating || nonProfit.rating}
          />
        )}
      </div>
      {/* {nonProfit?.isClaimed && (
        <div className="flex flex-row gap-2">
          <Image
            className="text-primary"
            src="/assets/images/neki-heart.png"
            alt="neki-score"
            width={28}
            height={24}
          />
          <Typography
            variant="subtitle1"
            color="textSecondary"
            className="line-clamp-1">
            Neki Score: {formatNumberForLocale(nonProfit?.company?.points)}
          </Typography>
          <div
            className="flex items-center justify-center cursor-pointer"
            onClick={handleShowInfo}>
            <MdInfo className="h-5 w-5 text-text-disabled" />
          </div>
        </div>
      )} */}
      <Typography variant="subtitle2" color="textSecondary">
        {npoLocation}
      </Typography>

      <div className="my-1 flex flex-row items-center gap-2">
        {nonProfit?.numFollowers > 0 && (
          <Typography variant="subtitle2" color="textSecondary">
            {nonProfit.numFollowers} follower
            {nonProfit?.numFollowers > 1 ? 's' : ''}
          </Typography>
        )}
      </div>

      <div className="inline-block w-fit">
        <PostMessage message={nonProfit.description} lineClamp={1} />
      </div>
      {nonProfit.giving_fund_status === 'ON_HOLD' && (
        <Typography variant="subtitle2" className="text-red-600">
          This organization is currently not in compliance with California
          regulations - please check back later for updates on their status.
        </Typography>
      )}
      {nonProfit.website_url && (
        <div className="flex items-center gap-2">
          <Typography
            variant="hyperlink"
            component="a"
            target="_blank"
            className="hover:underline"
            href={nonProfit.website_url}>
            {nonProfit.website_url}
          </Typography>
        </div>
      )}

      {!isEmpty(nonProfit.ein) && (
        <div className="my-1 flex items-center gap-2">
          <Typography variant="subtitle2" color="textSecondary">
            {nonProfit.country === 'IN' ? 'Pan #:' : 'EIN:'}
          </Typography>
          <Typography variant="subtitle2" color="textSecondary">
            {nonProfit.ein}
          </Typography>
        </div>
      )}

      {!isEmpty(nonProfit.certifications) && (
        <div className="my-2 flex flex-wrap items-center gap-2">
          {nonProfit.certifications.map((c) => (
            <div key={c.name}>
              {c.description?.length ? (
                <ClickableTooltipWrapper
                  message={c.description}
                  placement="top">
                  <Chip
                    className="cursor-pointer"
                    key={c.name}
                    color="secondary"
                    text="small">
                    {c.name}
                  </Chip>
                </ClickableTooltipWrapper>
              ) : (
                <Chip key={c.name} color="secondary" text="small">
                  {c.name}
                </Chip>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProfileTitle;
